import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import get from 'lodash/get';
import { useParams } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import GetAppIcon from '@material-ui/icons/GetApp';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import axios from 'axios';
import { toast } from 'react-toastify';
import { DropzoneDialog } from 'material-ui-dropzone';
import { EUROPE } from 'enums/regions';
import { getGlassChecklistPerformedFilesDownloadInfo } from 'utils/projectFiles';
import { putGlassCheckFile } from 'utils/warrantyFileLinks';
import {
  MAX_GLASS_CHECKLIST_FILES,
  uploadGlassChecklistFiles,
} from 'utils/glassChecklistFiles';

const API_URL = window.env.API_URL;
const ACCESS_TOKEN = window.env.ACCESS_TOKEN;
const toastConfig = { autoClose: 3000 };

const Glass = ({ values, getClient }) => {
  const intl = useIntl();
  const { code } = useParams();
  const [uploadOpen, setUploadOpen] = useState(false);

  const country = get(values, 'data.country');
  const isEuropeCountry = EUROPE.includes(country);
  const glassChecklistFiles = getGlassChecklistPerformedFilesDownloadInfo(
    values.data,
  );
  const remainingGlassChecklistFiles = Math.max(
    0,
    MAX_GLASS_CHECKLIST_FILES - glassChecklistFiles.length,
  );

  const handleDownloadGlassChecklistFile = async (file) => {
    if (!file?.uuid) return;
    try {
      const downloadUrl = `${API_URL}/bulk/download-stream/${encodeURIComponent(
        file.uuid,
      )}`;
      const response = await axios.get(downloadUrl, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem(ACCESS_TOKEN)}`,
        },
        responseType: 'blob',
      });
      const fallbackName =
        file.name ||
        (file.key && file.key.split('/').filter(Boolean).pop()) ||
        'certificado';
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fallbackName;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error al descargar el archivo:', error);
      toast.error(
        <FormattedMessage id="Hubo un error al descargar el archivo" />,
        toastConfig,
      );
    }
  };

  const handleUploadSave = async (files) => {
    const filesToUpload = files.slice(0, remainingGlassChecklistFiles);
    if (!filesToUpload.length || !code || typeof getClient !== 'function') {
      return;
    }
    try {
      const uploadedUuids = await uploadGlassChecklistFiles(filesToUpload);
      const existingUuids = glassChecklistFiles
        .map((file) => file.uuid)
        .filter(Boolean);
      const nextUuids = [...existingUuids, ...uploadedUuids].filter(
        (uuid, index, uuids) => uuids.indexOf(uuid) === index,
      );
      await putGlassCheckFile(code, nextUuids);
      setUploadOpen(false);
      toast.success(
        <FormattedMessage id="El archivo se envio correctamente" />,
        toastConfig,
      );
      getClient({ code });
    } catch (error) {
      console.error('Error al subir el archivo:', error);
      setUploadOpen(false);
      toast.error(
        <FormattedMessage id="Se a producido un error, vuelva a intentar" />,
        toastConfig,
      );
    }
  };

  const checklistSi = get(values, 'data.Checklist', '-').trim() === 'SI';

  return (
    <>
      <div className="row">
        <div className="col-12 ViewOrEdit">
          <div>
            <strong>
              <FormattedMessage id="Tipo de vidrio:" />
            </strong>
            <span>
              {get(values, 'data.glass') || '—'}
              {/* <FormattedMessage id={get(values, 'data.glass', '-')} /> */}
            </span>
          </div>
          <div className="kt-space-20" />
          <div>
            <strong>
              <FormattedMessage id="Realizó el Glass Checklist:" />
            </strong>
            <span>
              {get(values, 'data.Checklist') || '—'}
              {/* <FormattedMessage id={get(values, 'data.Checklist', '-')} /> */}
            </span>
          </div>
          <div className="kt-space-20" />

          {isEuropeCountry && checklistSi && (
            <div>
              <div className="kt-space-20" />
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 12,
                }}
              >
                {glassChecklistFiles.length > 0 ? (
                  glassChecklistFiles.map((file, index) => (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<GetAppIcon />}
                      disabled={!file.uuid}
                      key={file.uuid || file.key || index}
                      onClick={() => handleDownloadGlassChecklistFile(file)}
                    >
                      {file.name ||
                        `${intl.formatMessage({
                          id: 'Descargar certificado glass',
                        })} ${index + 1}`}
                    </Button>
                  ))
                ) : (
                  <span className="text-muted">
                    <FormattedMessage id="No hay certificado glass" />
                  </span>
                )}
                {remainingGlassChecklistFiles > 0 && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => setUploadOpen(true)}
                  >
                    <FormattedMessage id="Subir archivo a Amazon" />
                  </Button>
                )}
              </div>
              <DropzoneDialog
                acceptedFiles={['application/pdf', '.csv']}
                cancelButtonText={intl.formatMessage({ id: 'Cancel' })}
                submitButtonText={intl.formatMessage({ id: 'Send' })}
                dialogTitle={intl.formatMessage({ id: 'Upload file' })}
                dropzoneText={intl.formatMessage({
                  id: 'Drag and drop a file here or click',
                })}
                maxFileSize={5000000}
                open={uploadOpen}
                onClose={() => setUploadOpen(false)}
                onSave={handleUploadSave}
                showPreviews={false}
                showPreviewsInDropzone
                showFileNames
                filesLimit={Math.max(1, remainingGlassChecklistFiles)}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Glass;
